<template>
  <b-container fluid class="mb-4">
    <b-row>
      <Filters :attributes-filters="false" />
      <b-col class="search-section">
        <SearchHeader :default-sort="defaultSort" />
        <SponsorBanner
          v-if="!isPaidPlan"
          :query="query"
          :page="currentPage"
          :origin="$route.name"
        />
        <SearchWildcard />
        <WithoutResult :in-sort-page="true" />
        <GridCSS :items="searchResult">
          <template #default="slotProps">
            <NormalVectorOverlay :data="slotProps.data" />
          </template>
        </GridCSS>
        <FeedbackBanner v-if="isPaidPlan" />
        <PromotionalBanner v-else />
        <SearchPaginator
          :force-experience="forceExperience"
          :default-sort="defaultSort"
          class="mt-3"
        />
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { getOGImage } from '~/utils/common.js';

import Filters from '@/components/SearchResults/Filters';
import SearchHeader from '@/components/SearchResults/SearchHeader';
import SearchWildcard from '@/components/SearchResults/SearchWildcard';
import WithoutResult from '@/components/SearchResults/WithoutResult';
import GridCSS from '@/components/Common/GridCSS';
import NormalVectorOverlay from '@/components/Vectors/NormalVectorOverlay';
import SearchPaginator from '@/components/SearchResults/SearchPaginator';
import PromotionalBanner from '@/components/SearchResults/PromotionalBanner.vue';
import FeedbackBanner from '@/components/SearchResults/FeedbackBanner.vue';
import SponsorBanner from '@/components/SponsorBanner';

export default {
  components: {
    SearchPaginator,
    SearchWildcard,
    SearchHeader,
    Filters,
    GridCSS,
    NormalVectorOverlay,
    WithoutResult,
    PromotionalBanner,
    FeedbackBanner,
    SponsorBanner
  },
  props: {
    defaultSort: {
      type: String,
      required: true
    },
    dimension: {
      type: String,
      required: true
    },
    forceExperience: {
      type: String,
      required: false,
      default: ''
    },
    query: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      seoFields: { canonical: {} }
    };
  },
  async fetch() {
    let regexPages = /((-p)$|(-p-page))/;
    let slugName = this.getRouteBaseName().replace(regexPages, '');

    const slugs = {
      en: this.localePath({ name: slugName, params: { slug: this.$route.params.search } }, 'en'),
      de: this.localePath({ name: slugName, params: { slug: this.$route.params.search } }, 'de'),
      es: this.localePath({ name: slugName, params: { slug: this.$route.params.search } }, 'es'),
      pt: this.localePath({ name: slugName, params: { slug: this.$route.params.search } }, 'pt')
    };

    this.seoFields.canonical = this.$canonicalPlugin(
      slugs[`${this.$store.$i18n.locale}`],
      this.$store.$i18n.locale,
      true
    );
    if (this.getSilentSearch) {
      this.$store.commit('search/SET_SILENT_SEARCH', false);
    } else {
      await this.makeSearch();
    }
  },
  head() {
    const i18nSeo = this.$seoPlugin({ hreflang: false, canonical: false, slash: true });
    const openGraph = this.$openGraphPlugin(
      this.metadata?.metaTitle,
      'website',
      this.$route.path,
      this.metadata?.metaDescription,
      this.getOpenGraphImage,
      this.$store.state.i18n.locale
    );
    return {
      title: this.metadata?.metaTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.metadata?.metaDescription
        },
        {
          hid: 'robots',
          name: 'robots',
          content: this.hasLittleContent || this.isPaged ? 'noindex, follow' : 'all'
        },
        ...openGraph
      ],
      link: [this.seoFields.canonical, ...i18nSeo.link]
    };
  },
  computed: {
    ...mapState('search', [
      'searchResult',
      'wildcard',
      'searching',
      'currentPage',
      'totalPages',
      'metadata',
      'currentPage'
    ]),
    ...mapState('searchBar', ['actualSearch']),
    ...mapGetters('dimensions', ['getActualDimension']),
    ...mapGetters('search', ['getSilentSearch']),

    hasLittleContent() {
      return this.searchResult.length == 0;
    },
    isPaged() {
      return this.currentPage > 1;
    },
    getOpenGraphImage() {
      return getOGImage(this.forceExperience);
    },
    isPaidPlan() {
      return (
        this.$auth.user && this.$auth.user.idPlan !== 1 && !this.$auth.user.isFreeTrialSubscriptor
      );
    }
  },
  created() {
    this.$store.commit('dimensions/SET_PAGINATION_DIMENSION', this.dimension);
  },
  beforeDestroy() {
    if (!this.getSilentSearch) {
      this.$store.dispatch('search/CLEAN_SEARCH');
      this.$store.commit('dimensions/SET_PAGINATION_DIMENSION', null);
    }
  },
  methods: {
    makeSearch() {
      const currentPage = this.$route.params.page ? parseInt(this.$route.params.page) : 1;
      const searchPromise = this.$store.dispatch('search/MAKE_SEARCH', {
        sort: this.defaultSort,
        page: currentPage,
        experience: this.forceExperience
      });
      return Promise.all([searchPromise]);
    }
  }
};
</script>
